'use client';

import { FormEvent, useState, type CSSProperties } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { setCommentReaction, type ForumComment, type Stance } from './api';
import { EmojiReactions } from './EmojiReactions';
import { ForumCommandMenu } from './ForumCommandMenu';
import { StanceReactionButtons } from './StanceReactionButtons';
import './forum-actions.css';

export type CommentNode = ForumComment & { children: CommentNode[] };

export function buildCommentTree(comments: ForumComment[]): CommentNode[] {
  const byId = new Map<string, CommentNode>();
  const roots: CommentNode[] = [];
  for (const c of comments) {
    byId.set(c.id, { ...c, children: [] });
  }
  for (const c of comments) {
    const node = byId.get(c.id)!;
    if (c.parentId && byId.has(c.parentId)) {
      byId.get(c.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

type Props = {
  postId?: string;
  comments: ForumComment[];
  onReply?: (parentId: string, body: string) => Promise<void>;
  onUpdate?: () => void;
  canReply?: boolean;
};

function CommentItem({
  postId,
  node,
  depth,
  onReply,
  onUpdate,
  canReply,
}: {
  postId?: string;
  node: CommentNode;
  depth: number;
  onReply?: (parentId: string, body: string) => Promise<void>;
  onUpdate?: () => void;
  canReply?: boolean;
}) {
  const { isAuthenticated } = useAuth();
  const [replying, setReplying] = useState(false);
  const [body, setBody] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const hasReplies = node.children.length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!onReply || !body.trim()) return;
    await onReply(node.id, body.trim());
    setBody('');
    setReplying(false);
  }

  async function react(stance: Stance) {
    if (!isAuthenticated || !postId) return;
    const next = node.myStance === stance ? null : stance;
    await setCommentReaction(postId, node.id, next);
    onUpdate?.();
  }

  return (
    <li id={`comment-${node.id}`} className="comment-tree__item" style={{ '--comment-depth': depth } as CSSProperties}>
      <div className="comment-tree__row">
        {hasReplies ? (
          <button
            type="button"
            className="comment-tree__toggle btn btn-ghost"
            aria-expanded={expanded}
            aria-label={
              expanded
                ? 'Collapse replies'
                : `Expand ${node.children.length} ${node.children.length === 1 ? 'reply' : 'replies'}`
            }
            onClick={() => setExpanded((v) => !v)}
          >
            <i
              className={`uil uil-angle-down comment-tree__chevron${expanded ? ' comment-tree__chevron--open' : ''}`}
              aria-hidden
            />
          </button>
        ) : (
          <span className="comment-tree__toggle-spacer" aria-hidden />
        )}
        <div className="comment-tree__content">
          <div className="comment-tree__header">
            {!editing && <p className="comment-tree__body">{node.body}</p>}
            {postId && (
              <ForumCommandMenu
                kind="comment"
                postId={postId}
                comment={node}
                onUpdate={onUpdate}
                onEditingChange={setEditing}
              />
            )}
          </div>
          <div className="post-actions comment-tree__actions">
            {postId && (
              <StanceReactionButtons
                myStance={node.myStance}
                disabled={!isAuthenticated}
                onReact={react}
                testIdPrefix={`comment-${node.id}`}
              />
            )}
            {postId && <EmojiReactions postId={postId} commentId={node.id} onUpdate={onUpdate} />}
            <small className="text-muted">{new Date(node.createdAt).toLocaleString()}</small>
          </div>
          {canReply && onReply && (
            <button
              type="button"
              className="btn btn-ghost comment-tree__reply-btn"
              onClick={() => setReplying((v) => !v)}
            >
              Reply
            </button>
          )}
          {replying && (
            <form onSubmit={(e) => void submit(e)} className="comment-tree__reply-form">
              <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={2} />
              <button type="submit" className="btn btn-primary">
                Post reply
              </button>
            </form>
          )}
          {hasReplies && expanded && (
            <ul className="comment-tree">
              {node.children.map((child) => (
                <CommentItem
                  key={child.id}
                  postId={postId}
                  node={child}
                  depth={depth + 1}
                  onReply={onReply}
                  onUpdate={onUpdate}
                  canReply={canReply}
                />
              ))}
            </ul>
          )}
          {hasReplies && !expanded && (
            <button
              type="button"
              className="comment-tree__collapsed-hint btn btn-ghost"
              onClick={() => setExpanded(true)}
            >
              {node.children.length} {node.children.length === 1 ? 'reply' : 'replies'} hidden
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

export function CommentTree({ postId, comments, onReply, onUpdate, canReply }: Props) {
  const tree = buildCommentTree(comments);
  if (tree.length === 0) {
    return <p className="text-muted">No comments yet.</p>;
  }
  return (
    <ul className="comment-tree">
      {tree.map((node) => (
        <CommentItem key={node.id} postId={postId} node={node} depth={0} onReply={onReply} onUpdate={onUpdate} canReply={canReply} />
      ))}
    </ul>
  );
}
